import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import RegistrationDialog from "@/components/RegistrationDialog";
import { PORTAL_CATALOG_API } from "../../apis/apis";
import { encodeCode, getErr } from "../../components/portalUtils";
import { ErrorBox, Loader, safeUrl } from "../../components/PortalBits";

function ExploreOurCertification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [buying, setBuying] = useState(null); // { exam, region }
  const { user } = useOutletContext() || {};
  // Candidate's own region is shown first; both plans are always offered.
  const homeRegion = String(user?.region || "india").toLowerCase() === "india" ? "india" : "international";
  const priceText = (c, region) => {
    const v = region === "india" ? c.price_inr : c.price_usd;
    if (v === null || v === undefined) return "";
    return region === "india" ? `₹${Number(v).toLocaleString("en-IN")}` : `$${Number(v).toLocaleString("en-US")}`;
  };
  const plans = (c) =>
    [
      { region: "india", label: "India", note: "incl. GST" },
      { region: "international", label: "International", note: "" },
    ]
      .filter((p) => priceText(c, p.region))
      .sort((a, b) => (a.region === homeRegion ? -1 : b.region === homeRegion ? 1 : 0));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await PORTAL_CATALOG_API();
      if (res?.status === false) throw new Error(res?.message);
      setItems(res?.data || []);
    } catch (err) {
      setError(getErr(err, "Unable to load certifications."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const view = (c) =>
    navigate(`/user-panel/dashboard/${encodeCode(c.exam_code)}`, { state: { from: "/user-panel/explore-certifications" } });

  const openExternal = (c) => {
    const url = safeUrl(c.enroll_url);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <div className="page-head">
        <h2>Explore our certifications</h2>
        <div className="sub">Browse every Hacktonomy certification track.</div>
      </div>
      {loading && <Loader />}
      {!loading && error && <ErrorBox message={error} onRetry={load} />}
      {!loading && !error && items.length === 0 && (
        <div className="panel"><div className="empty-state">No certifications are published yet.</div></div>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="exam-grid">
          {items.map((c) => (
            <div key={c.exam_code} className="cert-tile">
              <div className="code">{c.exam_code}</div>
              <h3>{c.full_name}</h3>
              {c.description ? (
                <p
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {c.description}
                </p>
              ) : null}
              <div className="cert-foot">
                {c.enrolled ? (
                  <>
                    <span className="enrolled-pill">Enrolled</span>
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => view(c)}>View</button>
                  </>
                ) : c.purchasable && plans(c).length ? (
                  <div className="price-plans">
                    {plans(c).map((p) => (
                      <div key={p.region} className={`price-plan${p.region === homeRegion ? " is-home" : ""}`}>
                        <div>
                          <div className="price-plan-label">{p.label}</div>
                          <div className="price-plan-value">
                            {priceText(c, p.region)}
                            {p.note && <span className="price-plan-note"> {p.note}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`btn btn-sm ${p.region === homeRegion ? "btn-primary" : "btn-outline"}`}
                          onClick={() => setBuying({ exam: c, region: p.region })}
                        >
                          Enroll now
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <span className="li-meta">Not enrolled</span>
                    {safeUrl(c.enroll_url) ? (
                      <button type="button" className="btn btn-sm btn-primary" onClick={() => openExternal(c)}>Enroll now</button>
                    ) : (
                      <button type="button" className="btn btn-sm btn-outline" disabled>Coming soon</button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {buying && (
        <RegistrationDialog
          open={Boolean(buying)}
          onOpenChange={(v) => !v && setBuying(null)}
          region={buying.region}
          priceDisplay={priceText(buying.exam, buying.region)}
          examCode={buying.exam.exam_code}
          examName={buying.exam.full_name}
        />
      )}
    </>
  );
}

export default ExploreOurCertification;
